/**
 * scheduleConflicts — soft overlap detection for a guest's day.
 *
 * Shared by the add-service and reschedule flows: a candidate slot (a run or an
 * on-demand date/time) is checked against the guest's existing activities +
 * services on the same day. Overlap is a *warning*, never a hard block — the
 * operator decides. A run with no end time is treated as a `defaultDurationMin`
 * block so back-to-back transfers still surface.
 */

import type { GuestScheduleItem } from '../types';

export interface ConflictCandidate {
  date?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}

/** "HH:MM[:SS]" → minutes since midnight, or null when unparseable/untimed. */
function toMinutes(value?: string | null): number | null {
  if (!value) return null;
  const [h, m] = value.slice(0, 5).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Non-cancelled schedule items overlapping the candidate on the same day.
 * Both sides must be timed to intersect; untimed items never conflict (there is
 * nothing to overlap against).
 */
export function findScheduleConflicts(
  candidate: ConflictCandidate,
  schedule: GuestScheduleItem[],
  opts?: { ignoreId?: string; defaultDurationMin?: number },
): GuestScheduleItem[] {
  if (!candidate.date) return [];
  const dur = opts?.defaultDurationMin ?? 60;
  const cStart = toMinutes(candidate.startTime);
  if (cStart == null) return [];
  const cEnd = toMinutes(candidate.endTime) ?? cStart + dur;

  return schedule.filter((it) => {
    if (it.id === opts?.ignoreId) return false;
    if (it.status === 'cancelled') return false;
    if (it.date !== candidate.date) return false;
    const sStart = toMinutes(it.startTime);
    if (sStart == null) return false;
    const sEnd = toMinutes(it.endTime) ?? sStart + dur;
    return cStart < sEnd && sStart < cEnd; // half-open interval intersection
  });
}
